import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Fisioterapeuta } from '../types';
import { obtenerFisioterapeutas } from '../services/googleCalendarService';

const Container = styled.div`
  margin: 20px 0;
`;

const Title = styled.h2`
  color: #0078d4;
  font-size: 1.8rem;
  margin-bottom: 20px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 15px;
  }
`;

const FisioterapeutaList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FisioterapeutaCard = styled.div<{ selected: boolean }>`
  background-color: ${props => props.selected ? '#e6f2ff' : '#ffffff'};
  border: 2px solid ${props => props.selected ? '#0078d4' : '#e0e0e0'};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
    border-color: #0078d4;
  }
  
  @media (max-width: 480px) {
    flex-direction: row;
    text-align: left;
    padding: 15px;
  }
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #e6f2ff;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: #0078d4;
  
  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
    margin-bottom: 0;
    margin-right: 15px;
    flex-shrink: 0;
  }
`;

const FisioterapeutaInfo = styled.div`
  width: 100%;
`;

const FisioterapeutaName = styled.h3`
  margin: 0 0 10px 0;
  color: #333;
  font-size: 1.3rem;
  
  @media (max-width: 480px) {
    margin-bottom: 5px;
  }
`;

const FisioterapeutaSpecialty = styled.p`
  margin: 0;
  color: #666;
  font-size: 1rem;
`;

const SelectionIndicator = styled.div`
  font-size: 1.2rem;
  color: #0078d4;
  font-weight: 500;
  margin-top: 10px;
  
  @media (max-width: 480px) {
    margin-left: auto;
    margin-top: 0;
    padding-left: 10px;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 30px;
  color: #666;
  font-size: 1.1rem;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 20px;
  color: #d32f2f;
  background-color: #ffebee;
  border-radius: 8px;
  margin: 20px 0;
`;

interface FisioterapeutaSelectorProps {
  onSelectFisioterapeuta: (fisioterapeuta: Fisioterapeuta) => void;
  selectedFisioterapeutaId?: string;
}

const FisioterapeutaSelector: React.FC<FisioterapeutaSelectorProps> = ({
  onSelectFisioterapeuta,
  selectedFisioterapeutaId
}) => {
  const [fisioterapeutas, setFisioterapeutas] = useState<Fisioterapeuta[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cargarFisioterapeutas = async () => {
      try {
        setLoading(true);
        const data = await obtenerFisioterapeutas();
        const fisios = Array.isArray(data) ? data : [];
        console.log('Fisioterapeutas cargados:', fisios.length);
        setFisioterapeutas(fisios);
        setError(null);
      } catch (err) {
        setError('Error al cargar los fisioterapeutas. Por favor, inténtalo de nuevo más tarde.');
        console.error(err);
        setFisioterapeutas([]);
      } finally {
        setLoading(false);
      }
    };

    cargarFisioterapeutas();
  }, []);

  if (loading) {
    return <LoadingContainer>
      <div>Cargando fisioterapeutas...</div>
    </LoadingContainer>;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  // Obtener inicial del nombre para el avatar
  const getInitial = (nombre: string) => {
    return nombre.charAt(0).toUpperCase();
  };

  return (
    <Container>
      <Title>Selecciona un Fisioterapeuta</Title>
      {fisioterapeutas.length === 0 ? (
        <p>No hay fisioterapeutas disponibles en este momento.</p>
      ) : (
        <FisioterapeutaList>
          {fisioterapeutas.map((fisio) => (
            <FisioterapeutaCard
              key={fisio.id}
              selected={fisio.id === selectedFisioterapeutaId}
              onClick={() => onSelectFisioterapeuta(fisio)}
            >
              <Avatar>{getInitial(fisio.nombre)}</Avatar>
              <FisioterapeutaInfo>
                <FisioterapeutaName>{fisio.nombre}</FisioterapeutaName>
                <FisioterapeutaSpecialty>{fisio.especialidad}</FisioterapeutaSpecialty>
              </FisioterapeutaInfo>
              {fisio.id === selectedFisioterapeutaId && (
                <SelectionIndicator>✓</SelectionIndicator>
              )}
            </FisioterapeutaCard>
          ))}
        </FisioterapeutaList>
      )}
    </Container>
  );
};

export default FisioterapeutaSelector; 